import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, Sparkles, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { setAIRemaining } from './useAIStatus';

export const TONES = [
  { value: 'professionnel', label: 'Professionnel' },
  { value: 'chaleureux', label: 'Chaleureux' },
  { value: 'festif', label: 'Festif' },
  { value: 'percutant', label: 'Percutant' },
];
const LENGTHS = [
  { value: 'short', label: 'Courte' },
  { value: 'medium', label: 'Moyenne' },
  { value: 'long', label: 'Longue' },
];
export const LANGS = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'wo', label: 'Wolof' },
];
const CONTEXT_LABELS = {
  nomDeScene: 'Nom de scène', prenom: 'Prénom', nom: 'Nom', genreMusical: 'Genre musical', bio: 'Bio',
  title: 'Titre', eventTitle: 'Événement', postTitle: 'Titre du post', date: 'Date', location: 'Lieu',
  city: 'Ville', price: 'Prix', currency: 'Devise', ticketTypes: 'Billets', guests: 'Artistes invités', shopUrl: 'Lien',
};

export const aiErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return "L'assistant IA est momentanément indisponible";
};

const Pills = ({ options, value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        data-testid={`${testPrefix}-${o.value}`}
        className={`px-3 py-1 rounded-full text-xs border transition-colors ${value === o.value ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary'}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const AIGenerateDialog = ({ open, onOpenChange, api, field, context, onApply }) => {
  const [keyPoints, setKeyPoints] = useState('');
  const [tone, setTone] = useState('professionnel');
  const [length, setLength] = useState('medium');
  const [language, setLanguage] = useState('fr');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);

  const filledContext = Object.entries(context || {}).filter(([k, v]) => CONTEXT_LABELS[k] && v !== '' && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0));

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post('/ai/write', { mode: 'generate', field, context, key_points: keyPoints, tone, length, language });
      setResult(res.data.text);
      setAIRemaining(res.data.remaining);
    } catch (error) {
      toast.error(aiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => { if (!v) setResult(''); onOpenChange(v); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ai-generate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Générer avec l'IA</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {filledContext.length > 0 && (
            <div className="p-3 bg-muted/50 rounded-lg text-xs space-y-1" data-testid="ai-context-preview">
              <p className="font-semibold text-muted-foreground mb-1">Informations utilisées</p>
              {filledContext.map(([k, v]) => (
                <p key={k}><span className="text-muted-foreground">{CONTEXT_LABELS[k]} :</span> {Array.isArray(v) ? v.join(', ') : String(v).slice(0, 160)}</p>
              ))}
            </div>
          )}
          <div className="space-y-2">
            <Label>Points clés à mettre en avant (optionnel)</Label>
            <Textarea value={keyPoints} onChange={(e) => setKeyPoints(e.target.value)} rows={2} placeholder="Ex : retour sur scène après 2 ans, ambiance familiale, nouvel album..." data-testid="ai-key-points" />
          </div>
          <div className="space-y-2"><Label>Ton</Label><Pills options={TONES} value={tone} onChange={setTone} testPrefix="ai-tone" /></div>
          <div className="space-y-2"><Label>Longueur</Label><Pills options={LENGTHS} value={length} onChange={setLength} testPrefix="ai-length" /></div>
          <div className="space-y-2"><Label>Langue</Label><Pills options={LANGS} value={language} onChange={setLanguage} testPrefix="ai-lang" /></div>
          {result && (
            <div className="space-y-2">
              <Label>Aperçu</Label>
              <div className="p-3 rounded-lg border border-primary/40 bg-primary/5 text-sm whitespace-pre-wrap" data-testid="ai-result-preview">{result}</div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="ghost" onClick={() => close(false)} data-testid="ai-cancel-btn">Annuler</Button>
          {result ? (
            <>
              <Button type="button" variant="outline" onClick={generate} disabled={loading} data-testid="ai-regenerate-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Régénérer
              </Button>
              <Button type="button" onClick={() => { onApply(result); close(false); }} data-testid="ai-use-btn">Utiliser ce texte</Button>
            </>
          ) : (
            <Button type="button" onClick={generate} disabled={loading} data-testid="ai-generate-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Générer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
